import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { message, Spin } from 'antd';
import { KlarityService, ServerKlarityService, ServerMeta } from 'types/common';
import s from './s.module.less';
// import ProcessBar from '../../PPAOnboardingSteps/components/ProcessBar';
import Step1ServiceList from './components/Step1ServiceList';
import Step2SpecialtySelection from './components/Step2SpecialtySelection';
import Step3ServiceConfiguration from './components/Step3ServiceConfiguration';
import useFetchProvider from 'hooks/useFetchProvider';
import useGetProviderAllInsurance from 'hooks/useGetProviderAllInsurance';
import { INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE, INSURANCE_TYPE_INSURANCE } from 'constants/common';
import { saveProviderKlarityService } from 'api/klarityService';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { validationForInsuranceTable } from 'utils/serviceTemplate';

// 步骤枚举
export enum EServiceTemplateStep {
    STEP_1 = 'STEP_1', // Service list展示页面
    STEP_2 = 'STEP_2', // Specialties多选页面
    STEP_3 = 'STEP_3', // Service信息填写页面
}

// 数据类型定义
export interface ISpecialty {
    id: number;
    specialtyName: string;
    description: string;
    iconUrl: string;
    iconUri: string;
    recommended: boolean;
    serversTypes?: unknown[];
    services?: IService[];
}

export type TSpecialtySaveData = {
    providerKlarityService: ServerKlarityService[];
    disableIds: number[];
    conditions: string[];
};

// 使用KlarityService作为Specialty的类型

export interface IService {
    id: number;
    serviceType: string;
    serviceTypeId: number;
    customizedServiceName?: string;
    description: string;
    duration: number;
    earning: number;
    patientCost: number;
    teleHealth: boolean;
    inPerson: boolean;
    state: string;
    specialtyId: number;
    specialtyName: string;
}

export interface IServiceTemplateData {
    currentSpecialtyIndex: number;
    configuredServices: { [specialtyId: number]: IService[] };
}

const ServiceTemplateSetting: React.FC = () => {
    const [allStates] = useGetSettingData(EnumFields.STATE);
    const [currentStep, setCurrentStep] = useState<EServiceTemplateStep>(EServiceTemplateStep.STEP_1);
    const [tableData, tableDataLoading, refetchTableData] = useProviderKlarityServiceList();
    const [isEdit, setIsEdit] = useState(false);
    const [loading] = useState(false);
    const [provider] = useFetchProvider();
    const [uniprofileInsuranceData] = useGetProviderAllInsurance(provider?.id, false);
    const [serverIdStateMap, setServerIdStateMap] = useState<Record<string, ServerMeta[]>>({});
    const [, setPaymentMethod] = useState<
        typeof INSURANCE_TYPE_INSURANCE | typeof INSURANCE_TYPE_CASH | typeof INSURANCE_TYPE_CASH_AND_INSURANCE
    >();

    const [selectedSpecialties, setSelectedSpecialties] = useState<KlarityService[]>();
    const [templateData, setTemplateData] = useState<IServiceTemplateData>({
        currentSpecialtyIndex: 1,
        configuredServices: {},
    });
    // const [editingData, setEditingData] = useState<KlarityService[]>([]);
    const [editSpecialityValue, setEditSpecialityValue] = useState<ServerKlarityService[]>();

    // 步骤配置（暂时注释掉，后续可能需要）
    // const steps = useMemo(() => [
    //     { id: 1, title: 'Service List', step: EServiceTemplateStep.STEP_1 },
    //     { id: 2, title: 'Select Specialties', step: EServiceTemplateStep.STEP_2 },
    //     { id: 3, title: 'Configure Services', step: EServiceTemplateStep.STEP_3 },
    // ], []);

    // 当前步骤编号（暂时注释掉，后续可能需要）
    // const currentStepNum = useMemo(() => {
    //     return steps.findIndex((step) => step.step === currentStep) + 1;
    // }, [currentStep, steps]);

    useEffect(() => {
        if (currentStep === EServiceTemplateStep.STEP_1) {
            refetchTableData();
            setSelectedSpecialties([]);
        }
    }, [currentStep]);

    const handleSaveSpecialty = async (data: TSpecialtySaveData) => {
        try {
            const validResult = validationForInsuranceTable(data.providerKlarityService);
            // if (!validResult.isValid) {
            //     message.error(validResult.errors[0]);
            //     return;
            // }
            const res = await saveProviderKlarityService(
                data.providerKlarityService,
                data.disableIds || [],
                data.conditions || [],
            );
            if (res && !res.error) {
                // 检查是否还有未配置的Specialty
                const nextSpecialtyIndex = templateData.currentSpecialtyIndex + 1;
                if (nextSpecialtyIndex > (selectedSpecialties?.length || 0)) {
                    // 所有Specialty都配置完成，返回步骤1或完成流程
                    window.location.reload();
                } else {
                    // 进入下一个Specialty的配置
                    setTemplateData((prev) => ({
                        ...prev,
                        currentSpecialtyIndex: nextSpecialtyIndex,
                    }));
                }
            } else {
                message.error(res?.error);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // 下一步处理
    // const handleNext = useCallback(
    //     (saveData?: TSpecialtySaveData) => {
    //         switch (currentStep) {
    //             case EServiceTemplateStep.STEP_1:
    //                 setCurrentStep(EServiceTemplateStep.STEP_2);
    //                 break;
    //             case EServiceTemplateStep.STEP_2:
    //                 if (selectedSpecialties?.length && selectedSpecialties.length > 0) {
    //                     setCurrentStep(EServiceTemplateStep.STEP_3);
    //                 }
    //                 break;
    //             case EServiceTemplateStep.STEP_3:
    //                 if (saveData) {
    //                     handleSaveSpecialty(saveData);
    //                 }
    //                 break;
    //             default:
    //                 break;
    //         }
    //     },
    //     [currentStep, selectedSpecialties?.length, handleSaveSpecialty],
    // );
    const handleNext = (saveData?: TSpecialtySaveData) => {
        switch (currentStep) {
            case EServiceTemplateStep.STEP_1:
                setCurrentStep(EServiceTemplateStep.STEP_2);
                break;
            case EServiceTemplateStep.STEP_2:
                if (selectedSpecialties?.length && selectedSpecialties.length > 0) {
                    setCurrentStep(EServiceTemplateStep.STEP_3);
                }
                break;
            case EServiceTemplateStep.STEP_3:
                if (saveData) {
                    handleSaveSpecialty(saveData);
                }
                break;
            default:
                break;
        }
    };

    // 返回上一步
    const handleBack = useCallback(() => {
        if (isEdit) {
            setCurrentStep(EServiceTemplateStep.STEP_1);
        } else if (currentStep === EServiceTemplateStep.STEP_2) {
            setSelectedSpecialties([]);
            setCurrentStep(EServiceTemplateStep.STEP_1);
        } else if (currentStep === EServiceTemplateStep.STEP_3) {
            setCurrentStep(EServiceTemplateStep.STEP_2);
        }
    }, [currentStep, isEdit]);

    // 处理Specialty选择变化
    const handleSpecialtiesChange = useCallback((specialties: KlarityService[]) => {
        setSelectedSpecialties(specialties);
    }, []);

    // 处理添加新服务按钮点击
    const handleAddNewService = useCallback(() => {
        setCurrentStep(EServiceTemplateStep.STEP_2);
    }, []);

    // 处理服务配置变化
    const handleServiceConfigurationChange = useCallback((specialtyId: number, services: IService[]) => {
        setTemplateData((prev) => ({
            ...prev,
            configuredServices: {
                ...prev.configuredServices,
                [specialtyId]: services,
            },
        }));
    }, []);

    // 渲染当前步骤内容
    const renderStepContent = useMemo(() => {
        switch (currentStep) {
            case EServiceTemplateStep.STEP_1:
                return (
                    <Step1ServiceList
                        tableData={tableData}
                        tableDataLoading={tableDataLoading}
                        provider={provider!}
                        licenses={provider?.licenses}
                        allStates={allStates}
                        onAddNewService={handleAddNewService}
                        onEdit={() => {
                            setCurrentStep(EServiceTemplateStep.STEP_3);
                        }}
                        setIsEdit={setIsEdit}
                        isKiwiUser
                        setEditSpecialityValue={setEditSpecialityValue}
                        setEditingData={setSelectedSpecialties}
                        refetchTableData={refetchTableData}
                    />
                );
            case EServiceTemplateStep.STEP_2:
                return (
                    <Step2SpecialtySelection
                        tableData={tableData}
                        allStates={allStates}
                        setPaymentMethod={setPaymentMethod}
                        setServerIdStateMap={setServerIdStateMap}
                        onNext={handleNext}
                        onBack={handleBack}
                        selectedSpecialties={selectedSpecialties}
                        onSpecialtiesChange={handleSpecialtiesChange}
                    />
                );
            case EServiceTemplateStep.STEP_3:
                return (
                    <Step3ServiceConfiguration
                        editingValue={editSpecialityValue}
                        serverIdStateMap={serverIdStateMap}
                        isEdit={isEdit}
                        uniprofileInsuranceData={uniprofileInsuranceData}
                        provider={provider!}
                        onNext={handleNext}
                        onBack={handleBack}
                        selectedSpecialties={selectedSpecialties}
                        templateData={templateData}
                        onServiceConfigurationChange={
                            handleServiceConfigurationChange as (specialtyId: number, services: unknown[]) => void
                        }
                    />
                );
            default:
                return null;
        }
    }, [
        currentStep,
        tableData,
        tableDataLoading,
        provider,
        allStates,
        handleAddNewService,
        handleNext,
        handleBack,
        selectedSpecialties,
        handleSpecialtiesChange,
        editSpecialityValue,
        serverIdStateMap,
        isEdit,
        uniprofileInsuranceData,
        templateData,
        handleServiceConfigurationChange,
    ]);

    if (loading) {
        return <Spin spinning />;
    }

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                <div className={s.stepContent}>{renderStepContent}</div>
            </div>
        </div>
    );
};

export default ServiceTemplateSetting;
